/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * A window onto one line of the prescription.
 *
 * OCR returns a bounding box per medicine, so every row can show the exact strip
 * of handwriting it was read from. The pharmacist verifies the whole list
 * against the source by scanning down the page, without opening anything.
 *
 * Falls back to nothing when a box is missing — a row with no crop is fine, a
 * row showing the wrong crop is not.
 */
const RxCrop = ({ imageUrl, boundingBox, height = 56, pad = 1.25 }) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [natural, setNatural] = useState(null);

    const measure = useCallback(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    }, []);

    useEffect(() => {
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure]);

    if (!imageUrl || !boundingBox) return null;

    const { xmin, ymin, xmax, ymax } = boundingBox;
    const boxWidth = Math.max(1, xmax - xmin);
    const boxHeight = Math.max(1, ymax - ymin);

    let style = { opacity: 0 };

    if (natural && containerWidth) {
        // Scale so the box (plus breathing room) spans the container, but never
        // so far that a short box balloons past the window height.
        const scale = Math.min(
            containerWidth / (boxWidth * pad),
            height / (boxHeight * pad)
        );

        style = {
            width: natural.width * scale,
            height: natural.height * scale,
            left: -xmin * scale + (containerWidth - boxWidth * scale) / 2,
            top: -ymin * scale + (height - boxHeight * scale) / 2,
            opacity: 1,
        };
    }

    return (
        <div
            ref={containerRef}
            style={{ height }}
            className="relative w-full overflow-hidden rounded-md border border-cod-gray-100 bg-cod-gray-50"
        >
            <img
                src={imageUrl}
                alt=""
                aria-hidden="true"
                onLoad={(e) => {
                    setNatural({ width: e.target.naturalWidth, height: e.target.naturalHeight });
                    measure();
                }}
                style={style}
                className="pointer-events-none absolute max-w-none transition-opacity duration-200"
            />
        </div>
    );
};

export default RxCrop;
